import type { TicketConfigFormValues } from './schema'
import type { ITicketConfigFeature } from '@/shared/api/types/ticket-config-types'
import type { ITicketFeature } from '@/shared/api/types/ticket-feature-types'
import { Search } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useTicketFeaturesQuery } from '@/entities/ticket-feature'
import { useDashboardDisplayLanguage } from '@/shared/hooks/use-dashboard-display-language'
import { cn } from '@/shared/libs/cn'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/input'
import { Skeleton } from '@/shared/ui/skeleton'

interface FeaturesSectionProps {
  initialSelectedIds?: string[] | undefined
  initialFeatures?: ITicketConfigFeature[] | undefined
  selectionKey?: string | undefined
}

function resolveFeatureName(
  localizedData: ITicketFeature['localizedData'],
  languageCode: string,
  featureKey: string,
  fallbackLocalizedData?: ITicketConfigFeature['localizedData'],
): string {
  const preferred = localizedData[languageCode]
  if (preferred?.name?.trim()) return preferred.name.trim()
  if (preferred?.description?.trim()) return preferred.description.trim()
  const fallbackPreferred = fallbackLocalizedData?.[languageCode]
  if (fallbackPreferred?.name?.trim()) return fallbackPreferred.name.trim()
  if (fallbackPreferred?.description?.trim()) return fallbackPreferred.description.trim()
  for (const value of Object.values(localizedData)) {
    if (value?.name?.trim()) return value.name.trim()
  }
  for (const value of Object.values(localizedData)) {
    if (value?.description?.trim()) return value.description.trim()
  }
  for (const value of Object.values(fallbackLocalizedData ?? {})) {
    if (value?.name?.trim()) return value.name.trim()
  }
  for (const value of Object.values(fallbackLocalizedData ?? {})) {
    if (value?.description?.trim()) return value.description.trim()
  }
  return featureKey
}

export function FeaturesSection({
  initialSelectedIds,
  initialFeatures,
  selectionKey,
}: FeaturesSectionProps) {
  const { t } = useTranslation()
  const [keywords, setKeywords] = useState('')
  const { activeLanguage } = useDashboardDisplayLanguage()
  const { getValues, setValue } = useFormContext<TicketConfigFormValues>()
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => initialSelectedIds ?? getValues('ticketConfigFeatureIds') ?? [],
  )

  const { data, isLoading } = useTicketFeaturesQuery({
    current: 1,
    pageSize: 100,
    ...(keywords ? { keywords } : {}),
  })

  const features = (data?.data.data ?? []).filter((feature) => feature.isActive)
  const initialFeatureById = useMemo(() => {
    return new Map((initialFeatures ?? []).map((feature) => [feature.featureId, feature]))
  }, [initialFeatures])

  useEffect(() => {
    if (!selectionKey || !initialSelectedIds) {
      return
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resync local selection with external initial ids when the selection key changes
    setSelectedIds(initialSelectedIds)
    setValue('ticketConfigFeatureIds', initialSelectedIds)
  }, [initialSelectedIds, selectionKey, setValue])

  function onFeatureSelectionChange(next: string[]) {
    setSelectedIds(next)
    setValue('ticketConfigFeatureIds', next, {
      shouldDirty: true,
      shouldTouch: true,
    })
  }

  function setFeatureChecked(featureId: string, checked: boolean) {
    const next = checked
      ? Array.from(new Set([...selectedIds, featureId]))
      : selectedIds.filter((id) => id !== featureId)
    onFeatureSelectionChange(next)
  }

  function handleClearSelection() {
    onFeatureSelectionChange([])
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <Input
          placeholder={t('common.search')}
          value={keywords}
          onChange={(event) => setKeywords(event.target.value)}
          className="pl-9"
        />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">
          {t('ticketConfig.selectedOfTotal', {
            selected: selectedIds.length,
            total: features.length,
            defaultValue: '{{selected}} of {{total}} selected',
          })}
        </p>
        {selectedIds.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleClearSelection()}
          >
            {t('common.reset', { defaultValue: 'Reset' })}
          </Button>
        )}
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, index) => (
            // eslint-disable-next-line react/no-array-index-key -- skeleton placeholders have no stable identity
            <div key={`feature-skeleton-${index}`} className="flex items-center gap-3">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && features.length === 0 && (
        <EmptyState
          title={t('common.noData', { defaultValue: 'No data' })}
          description={t('ticketConfig.noFeaturesFound', {
            defaultValue: 'Try a different search term.',
          })}
        />
      )}

      {features.length > 0 && (
        <div className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white">
          {features.map((feature) => {
            const isSelected = selectedIds.includes(feature.id)
            return (
              <label
                key={feature.id}
                htmlFor={`feature-${feature.id}`}
                className={cn(
                  'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-zinc-50',
                  isSelected && 'bg-blue-50/40 hover:bg-blue-50/60',
                )}
              >
                <Checkbox
                  id={`feature-${feature.id}`}
                  checked={isSelected}
                  onCheckedChange={(checked) => setFeatureChecked(feature.id, checked === true)}
                />
                <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <Badge variant="secondary" className="w-fit font-mono text-[10px]">
                    {feature.featureKey}
                  </Badge>
                  <span className="text-sm font-medium text-zinc-900">
                    {resolveFeatureName(
                      feature.localizedData,
                      activeLanguage,
                      feature.featureKey,
                      initialFeatureById.get(feature.id)?.localizedData,
                    )}
                  </span>
                </div>
              </label>
            )
          })}
        </div>
      )}
    </div>
  )
}
